import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import DOMPurify from "dompurify"
import { SendHorizontal, Paperclip, SmilePlus } from "lucide-react"
import { toast } from "react-hot-toast"

import { useSessionContext } from '@/state/session-context'
import { getFeathersClient } from "./feathers-client"
import { ImageUploader } from "@/components/image-uploader"
import { userFacingErrorMessage } from "@/lib/user-facing-error"

import { useUserStore } from "@/state/user-store"
import { useChatStore } from '../../state/chat-store'
import { getColorSet } from "@/theme/color"
import { EmojiPicker } from "@/components/emoji-picker"
import { registerPendingSend, removePendingSend, setPendingSendUlid } from "@/state/pending-sends"
import { MessageService } from "../../../../shared-src/message-service"
import { attachmentRejectionReason } from "../../../../shared-src/attachment"
import { maxMessageTextLength } from "../../../../shared-src/message"

type MessageInput2Props = {
	maxImages?: number;
	disabled?: boolean;
	allowAttachments?: boolean;
	onError?: (msg: string) => void;
}

function toPlainTextFromHtml(html: string): string {
	const clean = DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
	const div = document.createElement("div")
	div.innerHTML = clean
	let txt = div.textContent || div.innerText || ""
	txt = txt.replace(/\u00a0/g, " ").replace(/[ \t]+/g, " ")
	txt = txt.replace(/\r\n?/g, "\n").replace(/\n{3,}/g, "\n\n").trim()
	return txt
}

export function MessageInput2(props: MessageInput2Props) {
	const [text, setText] = useState("")
	const [images, setImages] = useState<File[]>([])
	const fileInputRef = useRef<HTMLInputElement>(null)
	const textareaRef = useRef<HTMLTextAreaElement>(null)
	const emojiPickerRef = useRef<HTMLDivElement>(null)

	const maxImages = props.maxImages ?? 4

	const { session } = useSessionContext()
	const user = useUserStore(state => state.user)

	const feathersClient = getFeathersClient()
	const messagesService = feathersClient.service('messages')

	const activeChannelId = useChatStore(state => state.activeChannelId)
	const activeChannel = useChatStore(state => state.activeChannel)

	const colorSet = getColorSet({ senderColor: user?.color })

	const isReadOnly = activeChannel?.isReadOnly && !user?.isDeveloper
	const isLimitedMembership = activeChannel?.isLimitedMembership ?? false
	const isDisabled = props.disabled || isReadOnly
	const allowAttachments = props.allowAttachments ?? activeChannel?.usersCanSendImages ?? true

	let placeholder = "Type a message..."
	if (isReadOnly)
		placeholder = "You cannot send messages in this channel"
	if (isLimitedMembership)
		placeholder = "Subscribe to get full access to this group"

	const [showEmojiPicker, setShowEmojiPicker] = useState(false)

	const canSend = useMemo(
		() => {
			if (!activeChannelId) return false
			if (!session) return false
			if (isDisabled) return false
	
			return (text.trim().length > 0 || (allowAttachments && images.length > 0))
		}, 
		[text, images, isDisabled, allowAttachments, activeChannelId, session])
	
	const pushError = useCallback(
		(msg: string) => {
			if (props.onError)
				props.onError(msg)
			else
				toast.error(msg)
			console.warn(msg)
		},
		[props])

	const addImages = useCallback(
		(files: FileList | File[]) => {
			const arr = Array.from(files)
			const newImages: File[] = []
			for (const f of arr) {
				const reason = attachmentRejectionReason(f)
				if (reason) {
					pushError(reason)
					return
				}
				newImages.push(f)
			}
			if (images.length + newImages.length > maxImages) {
				pushError(`You can attach up to ${maxImages} image(s).`)
				return
			}
			setImages(prev => [...prev, ...newImages])
		}, 
		[images.length, maxImages, pushError])

	const onPaste = useCallback(
		(e: React.ClipboardEvent<HTMLTextAreaElement>) => {
			if (isDisabled) 	
				return

			const cd = e.clipboardData
			const files = cd.files
			const plain = cd.getData("text/plain") || ""
			const html = cd.getData("text/html") || ""

			if (files && files.length > 0) {
				if (!allowAttachments)
					return
				e.preventDefault()
				addImages(files)
				return
			}

			const incoming = html ? toPlainTextFromHtml(html) : plain
			if (!incoming.trim()) {
				e.preventDefault()
				return
			}

			e.preventDefault()
			const ta = e.currentTarget
			const start = ta.selectionStart ?? text.length
			const end = ta.selectionEnd ?? text.length
			const next = (text.slice(0, start) + incoming + text.slice(end)).slice(0, 5000)
			setText(next)
			requestAnimationFrame(() => {
				const pos = start + incoming.length
				ta.selectionStart = ta.selectionEnd = pos
			})
		},
		[addImages, isDisabled, text, allowAttachments])

	const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
		if (isDisabled) 
			return
		if (!allowAttachments) 
			return
		e.preventDefault()
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0)
			addImages(e.dataTransfer.files)
	}, [addImages, isDisabled, allowAttachments])

	const insertAtCursor = (text: string) => {
		const el = textareaRef.current
		if (!el) 
			return
		const start = el.selectionStart ?? el.value.length
		const end = el.selectionEnd ?? el.value.length
		const next = el.value.slice(0, start) + text + el.value.slice(end)
		setText(next)
		requestAnimationFrame(() => {
			const pos = start + text.length
			el.focus()
			el.setSelectionRange(pos, pos)
		})	
	}

	const sendMessage = useCallback(async () => {
		if (!activeChannelId) return
		if (!session) return

		//	the caller cleared the input the moment we were invoked, so a message that never lands
		//	would otherwise be lost silently along with its text and images. put them back -- but
		//	ONLY if the field/tray is still empty, since by now the user may have started typing or
		//	attaching the next one and overwriting that would be its own silent data loss. the
		//	functional updates keep the decision on the freshest state rather than on this closure's
		//	stale copy.
		//
		//	called from BOTH the refusal path and the catch below. it used to run only on refusal,
		//	inside the try, so a throw from uploadImage() jumped past it and the composer stayed
		//	empty -- text gone, photos gone, and "please try again" as the only feedback, which the
		//	user could not act on because there was nothing left in the box to retry (ha-625).
		const restoreComposer = () => {
			if (text.trim().length > 0)
				setText(prev => prev.length === 0 ? text : prev)
			if (images.length > 0)
				setImages(prev => prev.length === 0 ? images : prev)
		}

		//	returns the text shown to the user when the message was refused synchronously -- the
		//	server's rejection of the call, or the composed failure sentence from the catch -- else
		//	undefined. the caller treats any truthy return as "restore the composer".
		//
		//	an ordinary message goes through sendUserMessage(), which only queues it: a successful return means
		//	"accepted", not "stored". a later failure arrives as a `messages status` event and is
		//	handled by the pending-send registered below, which toasts and restores the composer itself.
		//	`!` commands still use create(), which answers synchronously.
		const sendSingleMessage = async (text?: string, attachmentIds?: string[]): Promise<string | undefined> => {
			if (!session) return
			if (!activeChannelId) return

			const userId = session.userId
			const message = {
				channelId: activeChannelId,
				senderType: 'user' as const,
				senderId: userId,
				senderName: user?.name,
				text,
				attachments: attachmentIds?.length ? attachmentIds.map(attachmentId => ({ attachmentId })) : undefined,
			}
	
			const isCommand = !!text?.trim().startsWith('!')
			const clientMessageId = isCommand ? undefined : crypto.randomUUID()
			if (clientMessageId)
				registerPendingSend(clientMessageId, reason => {
					pushError(reason)
					//	the composer now belongs to whatever channel is active; restoring there would
					//	put this message's text into the wrong conversation.
					if (useChatStore.getState().activeChannelId === message.channelId)
						restoreComposer()
				})

			try {
				if (clientMessageId) {
					const sendResult = await (messagesService as unknown as MessageService).sendUserMessage({ ...message, clientMessageId })
					setPendingSendUlid(clientMessageId, sendResult.ulid)
					return undefined
				}
				const result = await messagesService.create(message)
				//	this is the `!` path only. the server does NOT throw when it refuses a command --
				//	it RESOLVES with an error-shaped Message (senderType 'error'), for the rate limiter and for four
				//	other refusal paths in message-service.ts. Discarding the result therefore made
				//	every one of them invisible: the promise resolved, no catch ran, and the user's
				//	message vanished with no toast and nothing in the stream (ha-307). The UI has no
				//	renderer for senderType 'error', so the only way this reaches the user is here.
				if (result && (result as { senderType?: string }).senderType === 'error') {
					const detail = (result as { text?: string }).text || 'Message was not sent.'
					pushError(detail)
					return detail
				}
			} catch (error) {
				if (clientMessageId)
					removePendingSend(clientMessageId)
				console.error(error)
				//	every class that actually reaches here is a plain Error or a FeathersError
				//	subclass carrying a server-written sentence -- driven against the live socket
				//	for ha-630. The helper is display-equivalent for all of them; what it removes
				//	is the `'unknown error'` placeholder and the empty-message case that composed
				//	"Failed to send message: " and stopped mid-thought.
				//
				//	TypeError/SyntaxError are excluded by the helper but neither arrives on this
				//	path: the socket has no ackTimeout configured, so a dead connection BUFFERS
				//	the call until reconnect rather than rejecting, and nothing here parses a body.
				//
				//	toast and return the SAME string. The caller restores the composer on any
				//	truthy return, so returning the helper's output directly would hand back
				//	undefined for the classes it declines to show -- discarding the user's text
				//	and images on exactly those. Both branches below are non-empty literals, so
				//	the restore signal holds by construction rather than by luck.
				const detail = userFacingErrorMessage(error)
				const shown = detail ? `Failed to send message: ${detail}` : 'Failed to send message. Please try again.'
				pushError(shown)
				return shown
			}
		}

		const imageUploader = new ImageUploader()

		try {
			//	TODO show progress bar

			const attachmentIds: string[] = []
			for (const image of images) {
				const attachmentId = await imageUploader.uploadImage(image)
				attachmentIds.push(attachmentId)
			}

			//	one user action -> one sendUserMessage()/create() call, carrying the text and all attachments together
			//	(ha-520). splitting this into a text send plus a per-image send used to make the
			//	server's rate limiter reject the user's own images out from under them.
			let rejected: string | undefined
			if (text.trim().length > 0 || attachmentIds.length > 0)
				rejected = await sendSingleMessage(text, attachmentIds)

			if (rejected)
				restoreComposer()
		} catch (error) {
			console.error('Error sending message:', error)
			//	every throw from the upload loop lands here, and the cause is worth showing: a
			//	Feathers rejection from attachments.create, or the S3-POST failure thrown by
			//	uploadImage(). A fixed string discarded all of it and told the user to retry,
			//	which is the wrong advice for most of those causes (ha-625).
			//
			//	Both sides are authored literals -- `Upload failed: <statusText>` used to come
			//	through here and is gone (ha-627), and attachment-service.ts's three throws never
			//	interpolate the exception (ha-398, ha-633) -- so what `detail` carries is text
			//	written for a user, not whatever the platform happened to say.
			//
			//	TypeError is excluded because it is the ONE case the retry advice fits: fetch
			//	rejects with it when the connection dies, and its message is a browser string
			//	("Failed to fetch"), not something a server told us. `error.message ? ... :`
			//	alone would not catch that -- it guards emptiness, never provenance (ha-617).
			//	The test was written inline here first; it now lives in userFacingErrorMessage()
			//	because this was the third site to need it. That helper also excludes SyntaxError,
			//	which no path into THIS catch can throw -- uploadImage() never parses a body -- so
			//	sharing it changed nothing here beyond removing the second copy of the predicate.
			const detail = userFacingErrorMessage(error)
			pushError(detail ? `Failed to send message: ${detail}` : 'Failed to send message. Please try again.')
			restoreComposer()
		}

		//	deliberately NO setText('')/setImages([]) here (ha-307). the caller clears the input
		//	synchronously before any await; clearing again down here runs only AFTER the awaits
		//	above resolve, by which time the field may already hold the NEXT message the user has
		//	started typing -- and this wipes it. that produced an exactly-alternating drop at fast
		//	typing speed: a sent message's late clear empties the field for the following one, whose
		//	Enter then finds nothing to send and silently no-ops; because that no-op starts no send,
		//	no late clear is pending for the message after it, which goes through. hence every other
		//	one.
	}, [activeChannelId, text, session, images, pushError, messagesService, user?.name])

	const onSendClick = useCallback(
		() => {
			if (!canSend) 
				return
			sendMessage()
			setText("")
			setImages([])
		}, 
		[canSend, sendMessage, setText, setImages])

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault()
				onSendClick()
			}
		}, 
		[onSendClick])

	const removeImage = useCallback(
		(idx: number) => {
			setImages(prev => prev.filter((_, i) => i !== idx))
		}, 
		[])

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
				setShowEmojiPicker(false)
			}
		}

		if (showEmojiPicker) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [showEmojiPicker])
	
	return (
		<div
			className="w-full flex flex-col gap-2"
			onDragOver={e => e.preventDefault()}
			onDrop={onDrop}
		>
			{images.length > 0 && (
				<div className="flex flex-wrap gap-2">
					{images.map(
						(file, i) => {
							const url = URL.createObjectURL(file)
							return (
								<div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border">
									<img src={url} alt={`attachment-${i}`} className="w-full h-full object-cover" />
									<button
										type="button"
										className="absolute top-1 right-1 bg-black/60 text-white text-xs px-1 rounded"
										onClick={() => removeImage(i)}
										aria-label="Remove image"
									>
										✕
									</button>
								</div>
							)
						}
					)}
				</div>
			)}

			<div className="flex gap-3 items-center">
				{allowAttachments && (
					<>
						<input
							ref={fileInputRef}
							type="file"
							//	deliberately broad: narrowing this to the allowed types would hide HEIC files
							//	from the picker, and an iPhone user would get no explanation at all. leaving
							//	it selectable lets attachmentRejectionReason() tell them why it's refused.
							accept="image/*"
							multiple
							hidden
							onChange={e => {
								if (e.target.files) 
									addImages(e.target.files)
								if (fileInputRef.current) 
									fileInputRef.current.value = ""
							}}
						/>
						<button
							type="button"
							className="disabled:opacity-50"
							onClick={() => fileInputRef.current?.click()}
							disabled={isDisabled || images.length >= maxImages}
							title="Add image"
							style={{ color: colorSet.dark }}
						>
							<Paperclip className="h-5 w-5" />
						</button>
					</>
				)}

				<div className="relative" ref={emojiPickerRef}>
					<button			
						className="disabled:opacity-50"
						type="button"
						onClick={() => setShowEmojiPicker(!showEmojiPicker)}
						disabled={isDisabled}
						title="Emoji"
						style={{ color: colorSet.dark }}
					>
						<SmilePlus className="h-5 w-5 mt-1.5" />
					</button>

					{showEmojiPicker &&  (
						<div className="absolute bottom-full left-0 z-50 mb-2">
							<EmojiPicker
								onPick={(shortcode, emojiChar) => {
									insertAtCursor(`${emojiChar}`)
									setShowEmojiPicker(false)
								}}
							/>
						</div>
					)}
				</div>

				<textarea
					ref={textareaRef}
					className="flex-1 rounded-lg border p-2 px-4 outline-none min-h-[40px] shadow-messageInput disabled:opacity-50"
					placeholder={placeholder}
					value={text}
					onChange={e => setText(e.target.value)}
					onPaste={onPaste}
					onKeyDown={onKeyDown}
					disabled={isDisabled}
					maxLength={maxMessageTextLength}
					rows={1}
					style={{
						backgroundColor: colorSet.background,
						borderColor: colorSet.border,
						color: colorSet.text,
						fontSize: '16px'
					}}
				/>

				<button
					className="disabled:opacity-50"
					onClick={onSendClick}
					disabled={!canSend}
					style={{ color: colorSet.dark }}
				>
					<SendHorizontal className="h-5 w-5" />
				</button>
			</div>
		</div>
	)
}